/* Promoted columns — handover §6.2, and the design decision the custom record
 * engine turns on.
 *
 * A custom record keeps everything in one JSON payload, because types are
 * created and deleted at runtime and a column per field would mean the app
 * needed schema rights on the site. But a payload cannot be filtered: "every
 * custom record expiring in 90 days, across every type" would mean fetching
 * every row and parsing client-side.
 *
 * So four flagged values are copied out of the payload into real indexed
 * columns on write. Four, deliberately — each one is a value kept in two places
 * and every extra promotion target is another chance for the copy to drift from
 * the payload it came from.
 *
 * This lives here rather than in a provider because both providers do it, and
 * they did it in two copies of the same twenty lines. Promotion deciding one
 * thing in mock mode and another against SharePoint is precisely the kind of
 * difference that shows up as "it worked in dev".
 */

import type { ICustomRecord, IDepot, IRecordType } from '../models';

/** The promoted values for a record, derived from its type's flagged fields.
 *
 *  Empty values are skipped rather than written as blanks: a record whose
 *  expiry field has not been filled in yet should not read as expiring on the
 *  epoch, and clearing a promoted column is a different intent from never
 *  having set it.
 */
export function promotedValues(
  type: IRecordType | undefined,
  record: Pick<ICustomRecord, 'payload'>,
  depots: readonly IDepot[],
): Partial<ICustomRecord> {
  const promoted: Partial<ICustomRecord> = {};

  for (const field of type?.fields ?? []) {
    if (!field.promote) continue;
    const value = record.payload[field.id];
    if (value === undefined || value === null || value === '') continue;

    switch (field.promote) {
      case 'Depot': {
        /* The payload holds the depot's display name, because that is what the
           select offered. The column holds the id. A name that matches no depot
           is left unset rather than guessed at — see the note on toDepotId in
           services/sharepoint/lists.ts for what guessing costs. */
        const depot = depots.find((d) => d.name === String(value));
        if (depot) promoted.depot = depot.id;
        break;
      }
      case 'ExpiryDate':
        promoted.expiryDate = String(value);
        break;
      case 'Status':
        promoted.status = String(value);
        break;
      case 'OwnerLookup':
        promoted.ownerLookup = String(value);
        break;
    }
  }

  return promoted;
}

/** The record as it should be stored: promoted columns filled in, and the title
 *  taken from the type's first field.
 *
 *  Title is the first field by definition (§6.2) — it is the indexed column the
 *  list view and every deep link read, so it cannot be left to whatever the
 *  caller happened to pass.
 */
export function withPromotions(
  type: IRecordType | undefined,
  record: ICustomRecord,
  depots: readonly IDepot[],
): ICustomRecord {
  const first = type?.fields[0];
  return {
    ...record,
    ...promotedValues(type, record, depots),
    title: first ? String(record.payload[first.id] ?? record.title) : record.title,
  };
}

/** Which promotion targets a type has already spent.
 *
 *  Two fields promoting to the same column would mean the second silently wins,
 *  so the editor uses this to stop it being offered twice.
 */
export function usedPromotions(type: Pick<IRecordType, 'fields'>): Set<string> {
  const used = new Set<string>();
  for (const f of type.fields) if (f.promote) used.add(f.promote);
  return used;
}
